import { useCallback, useEffect, useMemo, useState } from 'react'

import { normalizeLanguage as normalize, QuoteI18nContext } from './quoteI18n'
import { LOCALES, quoteTranslations } from './translations'

/**
 * Lingua della pagina preventivo: da dove arriva e come si cambia.
 *
 * La pagina è pubblica e la apre il viaggiatore, quindi la lingua **non** può essere
 * `cap.language` (quella è la preferenza dell'operatore loggato: cambiarla da qui
 * girerebbe in inglese anche la dashboard di chi sta guardando). Si risolve invece così,
 * dal più forte al più debole:
 *
 * 1. **scelta manuale** fatta su questa pagina (persistita in `localStorage`)
 * 2. **`?lang=` nel link**, che l'email del preventivo porta con sé
 * 3. **lingua del preventivo**, cioè quella del messaggio del viaggiatore (dal record)
 * 4. **lingua del browser**
 * 5. italiano
 *
 * Il tema invece resta `cap.darkMode`: è già propagato su `<html data-theme>` da `App.jsx`
 * e i token CSS della pagina lo seguono da soli, quindi non c'è nulla da duplicare.
 */

/** Chiave della scelta manuale. Vale per tutti i preventivi: è una preferenza di lettura. */
const STORAGE_KEY = 'goelettQuoteLanguage'

const storedLanguage = () => {
  try {
    return normalize(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    // Safari in navigazione privata può rifiutare localStorage: si prosegue senza memoria.
    return undefined
  }
}

const urlLanguage = () => {
  try {
    return normalize(new URLSearchParams(window.location.search).get('lang'))
  } catch {
    return undefined
  }
}

const browserLanguage = () => normalize(navigator?.language)

/**
 * Rende disponibili lingua e testi a tutta la pagina.
 *
 * La lingua del preventivo non arriva come prop ma con `adoptQuoteLanguage`: il record si
 * legge dentro il provider (l'hook dei dati usa i testi tradotti per gli errori), quindi
 * il provider deve poter stare più in alto di chi lo scarica.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export function QuoteI18nProvider({ children }) {
  const [chosen, setChosen] = useState(() => storedLanguage())
  const [fromQuote, setFromQuote] = useState(undefined)
  const fromUrl = useMemo(() => urlLanguage(), [])

  const language = chosen ?? fromUrl ?? fromQuote ?? browserLanguage() ?? 'it'

  // La pagina è servita da un'app in italiano: senza questo, uno screen reader legge i
  // testi inglesi con la pronuncia italiana.
  useEffect(() => {
    document.documentElement.setAttribute('lang', language)
  }, [language])

  const changeLanguage = useCallback((next) => {
    const value = normalize(next)
    if (!value) return
    setChosen(value)
    try {
      window.localStorage.setItem(STORAGE_KEY, value)
    } catch {
      // Senza localStorage la scelta vale per la sessione corrente: accettabile.
    }
  }, [])

  /**
   * Registra la lingua letta dal preventivo. Non sovrascrive né la scelta manuale né il
   * `?lang=` del link: è solo il gradino successivo della catena.
   */
  const adoptQuoteLanguage = useCallback((value) => {
    const next = normalize(value)
    if (next) setFromQuote(next)
  }, [])

  const value = useMemo(
    () => ({
      language,
      locale: LOCALES[language],
      t: quoteTranslations[language],
      changeLanguage,
      adoptQuoteLanguage
    }),
    [language, changeLanguage, adoptQuoteLanguage]
  )

  return <QuoteI18nContext.Provider value={value}>{children}</QuoteI18nContext.Provider>
}
